/**
 * Native bridge
 *
 * Thin, dependency-light wrapper around Capacitor. Everything here is a no-op
 * in a normal browser, so the same source tree serves both the web PWA and the
 * native Android shell.
 *
 * Why dynamic imports: the `@capacitor/*` packages are only meaningful inside
 * the native WebView. Loading them lazily keeps them in a separate webpack
 * chunk that browsers never fetch, so the PWA pays no bundle cost for native
 * support. `isNativePlatform()` is a synchronous global check (the native
 * runtime injects `window.Capacitor` before any app code runs), so the guard
 * itself costs nothing.
 */

import { BRAND_DEFAULT_COLOR } from '../theme/palette';

/**
 * True only inside the Capacitor Android/iOS WebView.
 * @returns {boolean}
 */
export const isNativePlatform = () =>
  Boolean(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.());

/**
 * Current platform: 'android' | 'ios' | 'web'.
 * @returns {string}
 */
export const getPlatform = () =>
  (typeof window !== 'undefined' && window.Capacitor?.getPlatform?.()) || 'web';

// Cache the haptics module so a tap doesn't re-resolve the chunk every time.
let hapticsPromise = null;

/**
 * Light selection tick on tab/menu presses. Fire-and-forget, never throws.
 */
export const hapticSelect = () => {
  if (!isNativePlatform()) return;

  if (!hapticsPromise) {
    hapticsPromise = import('@capacitor/haptics').catch(() => null);
  }

  hapticsPromise
    .then((mod) => mod?.Haptics?.impact({ style: mod.ImpactStyle.Light }))
    .catch(() => {
      /* haptics are cosmetic – a failure must never surface to the user */
    });
};

/**
 * Wire up the Android hardware back button.
 *
 * Default Capacitor behaviour closes the whole app on any back press, which
 * feels broken in an SPA. Instead: walk the router history, and only exit when
 * there is nowhere left to go back to.
 */
const initBackButton = async () => {
  const { App } = await import('@capacitor/app');

  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack && window.history.length > 1) {
      window.history.back();
    } else {
      App.exitApp();
    }
  });
};

/**
 * Match the native status bar to the app chrome and dismiss the splash screen
 * once React has painted.
 */
const initChrome = async () => {
  const [{ StatusBar, Style }, { SplashScreen }] = await Promise.all([
    import('@capacitor/status-bar'),
    import('@capacitor/splash-screen'),
  ]);

  // Content renders below the bar (not edge-to-edge) so the app header is never
  // occluded; the bar itself picks up the brand colour.
  // Native chrome is painted before React has read the centre's config, so the
  // bar gets the product default rather than the tenant colour. Style.Dark is
  // Capacitor's "light text", which is what the dark garnet ground needs.
  await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
  await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
  await StatusBar.setBackgroundColor({ color: BRAND_DEFAULT_COLOR }).catch(() => {});

  await SplashScreen.hide().catch(() => {});
};

/**
 * Keep the layout usable when the soft keyboard opens: resize the WebView
 * rather than panning it, and drop the bottom nav while typing.
 */
const initKeyboard = async () => {
  const { Keyboard } = await import('@capacitor/keyboard');

  Keyboard.addListener('keyboardWillShow', () => {
    document.body.classList.add('lms-keyboard-open');
  });

  Keyboard.addListener('keyboardWillHide', () => {
    document.body.classList.remove('lms-keyboard-open');
  });
};

let initialized = false;

/**
 * Entry point called once from App. Safe to call on web (returns immediately)
 * and safe to call twice.
 */
export const initNative = async () => {
  if (!isNativePlatform() || initialized) return;
  initialized = true;

  // Lets CSS target the native shell specifically (e.g. status bar padding).
  document.body.classList.add('lms-native', `lms-native--${getPlatform()}`);

  await Promise.all([
    initChrome().catch((error) => console.warn('Native chrome init failed', error)),
    initBackButton().catch((error) => console.warn('Back button init failed', error)),
    initKeyboard().catch((error) => console.warn('Keyboard init failed', error)),
  ]);
};

export default initNative;
